import React, { useState } from 'react';
import { Loader2, Search, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

/**
 * DiscoveryStatusBanner - Shows discovery status to users
 * 
 * States:
 * - Searching: spinner + "Searching…"
 * - Ok: "X connections found"  
 * - Empty: "No matches yet — try widening filters"
 * - Error: "Something went wrong — tap to retry"
 * 
 * Admin-only: expandable debug details
 */
export const DiscoveryStatusBanner = ({ 
  isLoading = false,
  meta = null,
  debug = null,
  isAdmin = false,
  onRetry = null,
  compact = false,
}) => {
  const [showDebug, setShowDebug] = useState(false);
  
  // Determine status
  const status = isLoading ? 'searching' : (meta?.status || 'ok');
  
  // Get display content based on status
  const getStatusContent = () => {
    if (isLoading) {
      return {
        icon: <Loader2 className="w-4 h-4 animate-spin" />,
        message: 'Searching…',
        color: 'text-blue-400',
        bg: 'bg-blue-500/10 border-blue-500/20',
      };
    }
    
    switch (status) {
      case 'ok':
        return {
          icon: <CheckCircle2 className="w-4 h-4" />,
          message: meta?.message || `${meta?.result_count || 0} found`,
          color: 'text-emerald-400',
          bg: 'bg-emerald-500/10 border-emerald-500/20',
        };
      case 'empty':
        return {
          icon: <Search className="w-4 h-4" />,
          message: meta?.message || 'No matches yet — try widening filters',
          color: 'text-amber-400',
          bg: 'bg-amber-500/10 border-amber-500/20',
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          message: meta?.message || 'Something went wrong',
          color: 'text-red-400',
          bg: 'bg-red-500/10 border-red-500/20',
        };
      default:
        return {
          icon: <Search className="w-4 h-4" />,
          message: 'Ready',
          color: 'text-gray-400',
          bg: 'bg-gray-500/10 border-gray-500/20',
        };
    }
  };
  
  const { icon, message, color, bg } = getStatusContent();
  
  // Don't render if no meaningful state
  if (!isLoading && !meta) {
    return null;
  }
  
  return (
    <div 
      className={`rounded-lg border ${bg} transition-all duration-200`}
      data-testid="discovery-status-banner"
    >
      {/* Main banner row */}
      <div className={`flex items-center gap-2 ${compact ? 'px-3 py-1.5' : 'px-4 py-2'}`}>
        <span className={color}>{icon}</span>
        <span className={`${color} ${compact ? 'text-xs' : 'text-sm'} font-medium flex-1`}>
          {message}
        </span>
        
        {/* Retry button for errors */}
        {status === 'error' && onRetry && (
          <button
            onClick={onRetry}
            className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors"
            data-testid="discovery-retry-button"
          >
            <RefreshCw className="w-3 h-3" />
            Retry
          </button>
        )}
        
        {/* Admin debug toggle */}
        {isAdmin && debug && !isLoading && (
          <button
            onClick={() => setShowDebug(!showDebug)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-400 transition-colors"
            data-testid="discovery-debug-toggle"
          >
            Debug
            {showDebug ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
        
        {/* Request ID (subtle) */}
        {meta?.request_id && !compact && (
          <span className="text-[10px] text-gray-600 font-mono">
            {meta.request_id}
          </span>
        )}
      </div>
      
      {/* Admin debug panel */}
      {isAdmin && debug && showDebug && (
        <div 
          className="border-t border-gray-700/50 px-4 py-3 bg-black/20"
          data-testid="discovery-debug-panel"
        >
          <div className="text-xs text-gray-500 mb-2 font-medium">Debug Info (Admin Only)</div>
          <pre className="text-[10px] text-gray-400 font-mono overflow-x-auto whitespace-pre-wrap max-h-40 overflow-y-auto">
            {JSON.stringify(debug, null, 2)}
          </pre>
          {meta?.version && (
            <div className="mt-2 text-[10px] text-gray-600">
              Version: {meta.version}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DiscoveryStatusBanner;
